import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ReactDOM from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { DateInput } from '../components/ui/DateInput';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Users, Dumbbell, Apple, Flame, Beef, Wheat, Droplets,
  ArrowLeft, Trash2, ClipboardList, User, Copy, X, Library, Pencil, Search,
  Download, BookmarkPlus, Calculator
} from 'lucide-react';
import DeficitSurplusDialog from '../components/myClients/DeficitSurplusDialog';
import client from '../api/client';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipTrigger, TooltipContent } from '../components/ui/tooltip';

import CreateDietSuggestionModal from '../components/CreateDietSuggestionModal';
import ApplyDietTemplateSheet from '../components/ApplyDietTemplateSheet';
import ImportDietFromClientSheet from '../components/ImportDietFromClientSheet';
import SupplementPlanSheet from '../components/SupplementPlanSheet';

import ClientList from '../components/myClients/ClientList';
import { RoutineEditor, MUSCLE_GROUP_COLORS_MAP, getRoutineWorkSets, WorkSetsTags } from '../components/myClients/RoutineEditor';
import RoutineEditorFullScreen from '../components/myClients/RoutineEditorFullScreen';
import RoutineHistoryDialog from '../components/RoutineHistoryDialog';
import ClientCheckInsSheet from '../components/myClients/ClientCheckInsSheet';
import { ClientDetail } from '../components/myClients/ClientDetail';
import { formatDate } from '../lib/dateUtils';
import { useRoutableDialog } from '../hooks/useRoutableDialog';
import ListControls from '../components/ListControls';
import { applyClientSortFilter } from '../lib/clientListSort';
import useClientTags from '../hooks/useClientTags';
import ManageTagsModal from '../components/ManageTagsModal';
import TagChip from '../components/TagChip';
import { useSearchParams } from 'react-router-dom';

// Re-export named exports for backwards-compat (used by other files that may import from MyClientsPage)
export { MUSCLE_GROUP_COLORS_MAP, getRoutineWorkSets, WorkSetsTags, RoutineEditor, RoutineEditorFullScreen, ClientDetail };

// ==================== MAIN PAGE ====================
export default function MyClientsPage() {
  const { t } = useTranslation();
  const { user: authUser, refreshUser } = useAuth();
  const queryClient = useQueryClient();
  const clientDialog = useRoutableDialog('client');
  const editTargetsDialog = useRoutableDialog('edit');
  const suggestionDialog = useRoutableDialog('suggestion');
  const checkInDialog = useRoutableDialog('checkin');
  const supplementDialog = useRoutableDialog('supplement');
  const dietDialog = useRoutableDialog('diet');
  const routineDialog = useRoutableDialog('routine');
  const routineEditDialog = useRoutableDialog('routineEdit');
  const historyDialog = useRoutableDialog('history');
  const [selectedClient, setSelectedClient] = useState(null);

  // Dialogs
  const [showRoutineDialog, setShowRoutineDialog] = useState(false);
  const [showDietDialog, setShowDietDialog] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [checkInClient, setCheckInClient] = useState(null);
  const [assignRoutineId, setAssignRoutineId] = useState('');
  const [showCopyFromClient, setShowCopyFromClient] = useState(false);
  const [showApplyTemplate, setShowApplyTemplate] = useState(false);
  const [showImportFromClient, setShowImportFromClient] = useState(false);  const [dietTargets, setDietTargets] = useState({
    target_calories: 2000, target_protein: 150, target_carbs: 200, target_fat: 70
  });
  const [macroMode, setMacroMode] = useState('grams');
  const [macroPercents, setMacroPercents] = useState({ protein: 30, carbs: 40, fat: 30 });
  const [perDayMode, setPerDayMode] = useState(false);
  const [dietEndDate, setDietEndDate] = useState('');
  const [dietEndMode, setDietEndMode] = useState('days'); // 'days' | 'weeks' | 'months' | 'date'
  const [dietEndValue, setDietEndValue] = useState('');
  const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
  const DAY_LABELS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  const calcCals = (p, c, f) => Math.round((p || 0) * 4 + (c || 0) * 4 + (f || 0) * 9);
  // Auto-balance: when one % changes, redistribute remaining 100% proportionally among the other two
  const rebalancePct = (pct, changedKey, newVal) => {
    const clamped = Math.max(0, Math.min(100, newVal));
    const remaining = 100 - clamped;
    const others = ['protein','carbs','fat'].filter(k => k !== changedKey);
    const otherSum = others.reduce((s, k) => s + (pct[k] || 0), 0);
    const out = { ...pct, [changedKey]: clamped };
    if (otherSum === 0) {
      out[others[0]] = Math.round(remaining / 2);
      out[others[1]] = remaining - out[others[0]];
    } else {
      out[others[0]] = Math.round(remaining * (pct[others[0]] || 0) / otherSum);
      out[others[1]] = remaining - out[others[0]];
    }
    return out;
  };
  const defaultDayMacros = (base) => Object.fromEntries(DAYS.map(d => [d, { calories: base.target_calories, protein: base.target_protein, carbs: base.target_carbs, fat: base.target_fat }]));
  const [dailyMacros, setDailyMacros] = useState(() => defaultDayMacros({ target_calories: 2000, target_protein: 150, target_carbs: 200, target_fat: 70 }));
  const defaultDayPercents = (base) => Object.fromEntries(DAYS.map(d => [d, { protein: 30, carbs: 40, fat: 30 }]));
  const [perDayPercents, setPerDayPercents] = useState(() => defaultDayPercents());
  // Draft states for Kcal inputs — hold the raw typed string so intermediate keystrokes don't trigger scaling
  const [calDraftSame, setCalDraftSame] = useState(null); // null = not editing
  const [calDraftPerDay, setCalDraftPerDay] = useState({}); // { day: string }

  const [showDeficitCalc, setShowDeficitCalc] = useState(false);
  const [showCreateSuggestion, setShowCreateSuggestion] = useState(false);
  const [editingSuggestion, setEditingSuggestion] = useState(null);
  const [removeConfirmClient, setRemoveConfirmClient] = useState(null);
  const [supplementClient, setSupplementClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const sortBy  = searchParams.get('sort')   || 'name';
  const sortDir = searchParams.get('dir')    || 'asc';
  const filter  = searchParams.get('filter') || 'all';
  const setParam = (key, val, def) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    if (val === def) next.delete(key); else next.set(key, val);
    return next;
  }, { replace: true });
  // Direct routine editor (opens from client card without going to ClientDetail)
  const [directRoutineEditor, setDirectRoutineEditor] = useState(null); // { clientUser, routine }

  const MEAL_TYPES = [
    { value: 'breakfast', label: t('diet.breakfast'), icon: 'AM' },
    { value: 'lunch', label: t('diet.lunch'), icon: 'PM' },
    { value: 'dinner', label: t('diet.dinner'), icon: 'EVE' },
    { value: 'snack', label: t('diet.snacks'), icon: 'SNK' },
  ];

  const { data: clientsData, isLoading: loading } = useQuery({
    queryKey: ['my-clients-and-routines'],
    queryFn: async () => {
      const [clientsRes, routinesRes] = await Promise.all([
        client.get('/users/my-clients'),
        client.get('/routines')
      ]);
      return { clients: clientsRes.data, routines: routinesRes.data };
    },
  });

  const clients = clientsData?.clients || [];
  const routines = clientsData?.routines || [];

  useEffect(() => {
    if (!clientDialog.id) {
      if (selectedClient !== null) setSelectedClient(null);
      return;
    }
    const match = clients.find(c => c.id === clientDialog.id);
    if (match && match.id !== selectedClient?.id) {
      setSelectedClient(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientDialog.id, clients]);

  useEffect(() => {
    setShowDeficitCalc(editTargetsDialog.id === 'targets');
  }, [editTargetsDialog.id]);

  useEffect(() => {
    if (!suggestionDialog.id) {
      if (editingSuggestion !== null) setEditingSuggestion(null);
      return;
    }
    if (editingSuggestion?.id !== suggestionDialog.id) {
      const match = clientSuggestions.find(s => s.id === suggestionDialog.id);
      setEditingSuggestion(match || { id: suggestionDialog.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestionDialog.id]);

  useEffect(() => {
    if (!checkInDialog.id) {
      if (checkInClient !== null) setCheckInClient(null);
      return;
    }
    if (checkInClient?.id !== checkInDialog.id) {
      const match = clients.find(c => c.id === checkInDialog.id);
      setCheckInClient(match || { id: checkInDialog.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInDialog.id, clients]);

  useEffect(() => {
    if (!supplementDialog.id) {
      if (supplementClient !== null) setSupplementClient(null);
      return;
    }
    if (supplementClient?.id !== supplementDialog.id) {
      const match = clients.find(c => c.id === supplementDialog.id);
      setSupplementClient(match || { id: supplementDialog.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplementDialog.id, clients]);

  useEffect(() => {
    setShowDietDialog(dietDialog.id === 'setup');
  }, [dietDialog.id]);

  useEffect(() => {
    setShowRoutineDialog(routineDialog.id === 'assign');
  }, [routineDialog.id]);

  useEffect(() => {
    if (!routineEditDialog.id) {
      if (directRoutineEditor !== null) setDirectRoutineEditor(null);
      return;
    }
    if (directRoutineEditor?.routine?.id !== routineEditDialog.id) {
      setDirectRoutineEditor({ routine: { id: routineEditDialog.id } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routineEditDialog.id]);

  const searchedClients = clients.filter(c => {
    const q = searchQuery.toLowerCase();
    if (!q) return true;
    return (
      c.name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q)
    );
  });
  const filteredClients = applyClientSortFilter(searchedClients, { sortBy, sortDir, filter });

  // Tags: owner-scoped labels used for both the tag filter and the group-by view.
  const { tags, refetch: refetchTags } = useClientTags();
  const [manageTagsOpen, setManageTagsOpen] = useState(false);
  const groupBy = searchParams.get('group') === 'tag';
  const tagFilter = searchParams.getAll('tag');
  const onTagsChanged = () => {
    queryClient.invalidateQueries({ queryKey: ['my-clients-and-routines'] });
    refetchTags();
  };
  const onTagCreated = () => refetchTags();
  const toggleTagFilter = (tagId) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    const cur = next.getAll('tag');
    next.delete('tag');
    (cur.includes(tagId) ? cur.filter(x => x !== tagId) : [...cur, tagId]).forEach(v => next.append('tag', v));
    return next;
  }, { replace: true });

  const visibleClients = tagFilter.length
    ? filteredClients.filter(c => (c.tags || []).some(tg => tagFilter.includes(tg.id)))
    : filteredClients;

  const handlePin = async (subjectId, pinned) => {
    try {
      await client.post('/client-tags/pin', { subject_id: subjectId, pinned });
      queryClient.invalidateQueries({ queryKey: ['my-clients-and-routines'] });
    } catch {
      toast.error(t('clientTags.pinFailed', 'Could not update pin'));
    }
  };
  // Grouping wins over pinning: inside a tag group the pin order is not applied.
  const orderedClients = groupBy
    ? visibleClients
    : [...visibleClients].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));

  const { data: clientDetails, isLoading: detailsLoading, isError: clientDetailsError } = useQuery({
    queryKey: ['client-details', selectedClient?.id],
    queryFn: async () => {
      const res = await client.get(`/users/client/${selectedClient.id}/details`);
      return res.data;
    },
    enabled: !!selectedClient,
  });

  useEffect(() => {
    if (clientDetailsError) toast.error(t('clients.failedLoadDetails'));
  }, [clientDetailsError]);

  const { data: clientRoutinesList = [] } = useQuery({
    queryKey: ['client-routines-list'],
    queryFn: async () => {
      const res = await client.get('/routines/client-routines');
      return res.data || [];
    },
    enabled: showRoutineDialog,
  });

  const { data: clientSuggestionsData } = useQuery({
    queryKey: ['client-suggestions', editClient?.id],
    queryFn: async () => {
      const res = await client.get(`/diet-suggestions?client_id=${editClient.id}`);
      return res.data.filter(s => s.active !== false);
    },
    enabled: !!(showDietDialog && editClient?.id),
  });

  const clientSuggestions = clientSuggestionsData || [];

  const { data: pendingCountsData } = useQuery({
    queryKey: ['pendingReviewsCount'],
    queryFn: async () => {
      const res = await client.get('/measurements/pending-reviews/count');
      return res.data.by_client || {};
    },
    retry: false,
  });
  const pendingCounts = pendingCountsData || {};

  const openClientDetails = (c) => {
    clientDialog.open(c.id);
  };

  const openRoutineDialog = (c) => {
    setEditClient(c);
    setAssignRoutineId('none');
    setShowCopyFromClient(false);
    routineDialog.open('assign');
  };

  const handleRemoveClient = (c) => {
    setRemoveConfirmClient(c);
  };

  const confirmRemoveClientMutation = useMutation({
    mutationFn: (clientToRemove) => client.put(`/users/${clientToRemove.id}/assign-coach?coach_id=none`),
    onSuccess: (_, clientToRemove) => {
      toast.success(`${clientToRemove.name} removed from your clients`);
      setRemoveConfirmClient(null);
      queryClient.invalidateQueries({ queryKey: ['my-clients-and-routines'] });
    },
    onError: (err) => { toast.error(err?.response?.data?.detail || 'Failed to remove client'); },
  });

  const confirmRemoveClient = () => {
    if (removeConfirmClient) confirmRemoveClientMutation.mutate(removeConfirmClient);
  };

  const handleAssignRoutineMutation = useMutation({
    mutationFn: () => {
      const rid = assignRoutineId === 'none' ? '' : assignRoutineId;
      return client.put(`/users/${editClient.id}/assign-routine?routine_id=${rid}`);
    },
    onSuccess: () => {
      const rid = assignRoutineId === 'none' ? '' : assignRoutineId;
      toast.success(rid ? 'Routine assigned & customized for client' : 'Routine removed');
      routineDialog.close();
      queryClient.invalidateQueries({ queryKey: ['my-clients-and-routines'] });
      if (selectedClient?.id === editClient.id) queryClient.invalidateQueries({ queryKey: ['client-details', editClient.id] });
    },
    onError: () => { toast.error(t('clients.failedAssignRoutine')); },
  });

  const handleAssignRoutine = () => handleAssignRoutineMutation.mutate();

  const handleCopyClientRoutineMutation = useMutation({
    mutationFn: async (sourceRoutine) => {
      // assign-routine already clones the source routine for the client
      await client.put(`/users/${editClient.id}/assign-routine?routine_id=${sourceRoutine.id}`);
      return { sourceRoutine };
    },
    onSuccess: ({ sourceRoutine }) => {
      toast.success(`Copied "${sourceRoutine.name}" from ${sourceRoutine.client_name} to ${editClient.name}`);
      routineDialog.close();
      queryClient.invalidateQueries({ queryKey: ['my-clients-and-routines'] });
      if (selectedClient?.id === editClient.id) queryClient.invalidateQueries({ queryKey: ['client-details', editClient.id] });
    },
    onError: () => { toast.error(t('clients.failedCopyRoutine')); },
  });

  const handleCopyClientRoutine = (sourceRoutine) => handleCopyClientRoutineMutation.mutate(sourceRoutine);

  // Direct routine editor — open from client card
  const openDirectRoutineEditor = async (clientUser) => {
    if (!clientUser.routine_id) return;
    try {
      const res = await client.get(`/users/${clientUser.id}/routine`);
      setDirectRoutineEditor({ clientUser, routine: res.data });
      routineEditDialog.open(res.data.id);
    } catch {
      toast.error(t('clients.failedLoadRoutine'));
    }
  };

  const saveDirectRoutineMutation = useMutation({
    mutationFn: ({ clientId, editedRoutine, sessionId }) => client.put(`/users/${clientId}/routine${sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : ''}`, editedRoutine),
    onSuccess: (_, { clientId }) => {
      toast.success(t('clients.routineSaved'));
      queryClient.invalidateQueries({ queryKey: ['my-clients-and-routines'] });
      if (selectedClient?.id === clientId) queryClient.invalidateQueries({ queryKey: ['client-details', clientId] });
    },
    onError: () => { toast.error(t('clients.failedUpdateRoutine')); },
  });

  const handleUpdateDietMutation = useMutation({
    mutationFn: () => {
      const finalTargets = {
        ...dietTargets,
        target_calories: macroMode === 'grams'
          ? calcCals(dietTargets.target_protein, dietTargets.target_carbs, dietTargets.target_fat)
          : dietTargets.target_calories,
      };
      let finalDailyMacros = dailyMacros;
      if (perDayMode && macroMode === 'grams') {
        finalDailyMacros = Object.fromEntries(
          DAYS.map(d => {
            const day = dailyMacros[d] || {};
            return [d, { ...day, calories: calcCals(day.protein, day.carbs, day.fat) }];
          })
        );
      }
      const payload = {
        ...finalTargets,
        daily_macro_targets: perDayMode ? finalDailyMacros : null,
        diet_end_date: dietEndDate || null,
        diet_start_date: dietEndDate ? new Date().toISOString().split('T')[0] : null,
      };
      return client.put(`/users/${editClient.id}/diet`, payload);
    },
    onSuccess: () => {
      toast.success(t('clients.dietTargetsUpdated'));
      dietDialog.close();
      queryClient.invalidateQueries({ queryKey: ['my-clients-and-routines'] });
      if (selectedClient?.id === editClient.id) queryClient.invalidateQueries({ queryKey: ['client-details', editClient.id] });
    },
    onError: () => { toast.error(t('clients.failedUpdateDiet')); },
  });

  const computeEndDate = (mode, value) => {
    if (!value) return '';
    if (mode === 'date') return value;
    const n = parseInt(value, 10);
    if (isNaN(n) || n <= 0) return '';
    const d = new Date();
    if (mode === 'days') d.setDate(d.getDate() + n);
    else if (mode === 'weeks') d.setDate(d.getDate() + n * 7);
    else if (mode === 'months') d.setMonth(d.getMonth() + n);
    return d.toISOString().split('T')[0];
  };

  const toggleCoachDietMutation = useMutation({
    mutationFn: ({ clientId, active }) => client.put(`/users/${clientId}/diet/toggle-active`, { active }),
    onSuccess: (_, { clientId, active }) => {
      toast.success(active ? 'Diet protocol activated' : 'Diet protocol deactivated');
      queryClient.invalidateQueries({ queryKey: ['my-clients-and-routines'] });
      if (selectedClient?.id === editClient?.id) queryClient.invalidateQueries({ queryKey: ['client-details', editClient?.id] });
      // Refresh auth user if coach is editing their own account
      if (clientId === authUser?.id) refreshUser();
    },
    onError: () => { toast.error(t('clients.failedToggleDiet')); },
  });

  const handleUpdateDiet = () => handleUpdateDietMutation.mutate();

  const deleteDietSuggestionMutation = useMutation({
    mutationFn: (id) => client.delete(`/diet-suggestions/${id}`),
    onMutate: async (id) => {
      const key = ['client-suggestions', editClient?.id];
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData(key);
      queryClient.setQueryData(key, (old) => (old || []).filter(s => s.id !== id));
      return { prev, key };
    },
    onSuccess: () => {
      toast.success(t('clients.suggestionRemoved'));
    },
    onError: (_err, _id, context) => {
      if (context?.prev) queryClient.setQueryData(context.key, context.prev);
      toast.error(t('clients.failedRemoveSuggestion'));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['client-suggestions', editClient?.id] });
    },
  });

  const deleteDietSuggestion = (id) => deleteDietSuggestionMutation.mutate(id);

  const saveToTemplate = async (suggestion) => {
    try {
      await client.post('/diet-templates', {
        name: suggestion.name,
        notes: suggestion.notes || '',
        meals: (suggestion.meals || []).map(m => ({
          meal_name: m.meal_type || m.meal_name || '',
          meal_type: m.meal_type || m.meal_name || '',
          option_name: m.option_name || null,
          foods: m.foods || [],
        })),
      });
      toast.success(t('clients.savedAsTemplate', { name: suggestion.name }));
    } catch {
      toast.error(t('clients.failedSaveTemplate'));
    }
  };

  const openDietDialog = (c) => {
    setEditClient(c);
    const tp = c.target_protein || 150;
    const tc = c.target_carbs || 200;
    const tf = c.target_fat || 70;
    const base = {
      target_calories: c.target_calories || calcCals(tp, tc, tf),
      target_protein: tp,
      target_carbs: tc,
      target_fat: tf,
    };
    setDietTargets(base);
    setMacroMode('grams');
    if (c.daily_macro_targets && Object.keys(c.daily_macro_targets).length > 0) {
      setPerDayMode(true);
      const merged = { ...defaultDayMacros(base), ...c.daily_macro_targets };
      setDailyMacros(merged);
      setPerDayPercents(Object.fromEntries(DAYS.map(d => {
        const day = merged[d] || base;
        const cal = day.calories || 1;
        return [d, {
          protein: Math.round((day.protein || 0) * 4 / cal * 100) || 30,
          carbs: Math.round((day.carbs || 0) * 4 / cal * 100) || 40,
          fat: Math.round((day.fat || 0) * 9 / cal * 100) || 30,
        }];
      })));
    } else {
      setPerDayMode(false);
      setDailyMacros(defaultDayMacros(base));
      setPerDayPercents(defaultDayPercents());
    }
    if (c.diet_end_date) {
      setDietEndMode('date');
      setDietEndValue(c.diet_end_date);
    } else {
      setDietEndMode('days');
      setDietEndValue('');
    }
    setDietEndDate(c.diet_end_date || '');
    dietDialog.open('setup');
  };

  if (loading || detailsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <>
      {selectedClient && clientDetails ? (
        <ClientDetail
          clientDetails={clientDetails}
          onBack={() => { clientDialog.close(); }}
          onRoutine={openRoutineDialog}
          onDiet={openDietDialog}
          onSupplements={(client) => supplementDialog.open(client.id)}
          onRefresh={() => queryClient.invalidateQueries({ queryKey: ['client-details', selectedClient?.id] })}
        />
      ) : (
        <>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder={t('myClients.searchPlaceholder')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="clients-search-input"
            />
          </div>
          <ListControls
            sortBy={sortBy}   onSortByChange={v => setParam('sort', v, 'name')}
            sortDir={sortDir} onSortDirChange={v => setParam('dir', v, 'asc')}
            filter={filter}   onFilterChange={v => setParam('filter', v, 'all')}
            sortOptions={[
              { value: 'name',     label: t('clients.sort.name', 'Name') },
              { value: 'calories', label: t('clients.sort.calories', 'Calories') },
              { value: 'added',    label: t('clients.sort.added', 'Date added') },
              { value: 'routine',  label: t('clients.sort.routine', 'Routine status') },
            ]}
            filterOptions={[
              { value: 'all',         label: t('clients.filter.all', 'All clients') },
              { value: 'has_routine', label: t('clients.filter.hasRoutine', 'Has routine') },
              { value: 'no_routine',  label: t('clients.filter.noRoutine', 'No routine') },
              { value: 'has_diet',    label: t('clients.filter.hasDiet', 'Has diet') },
              { value: 'no_diet',     label: t('clients.filter.noDiet', 'No diet') },
            ]}
          >
            <Button variant={groupBy ? 'default' : 'outline'} size="sm"
              onClick={() => setParam('group', groupBy ? '' : 'tag', '')} data-testid="group-by-tag">
              {t('clientTags.groupByTag', 'Group by tag')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setManageTagsOpen(true)} data-testid="manage-tags">
              {t('clientTags.manage', 'Manage tags')}
            </Button>
          </ListControls>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {tags.map(tg => (
                <button key={tg.id} type="button" data-testid={`tag-filter-${tg.id}`}
                  onClick={() => toggleTagFilter(tg.id)}
                  className={tagFilter.includes(tg.id) ? 'ring-2 ring-primary rounded-full' : 'opacity-70 hover:opacity-100'}>
                  <TagChip tag={tg} />
                </button>
              ))}
            </div>
          )}
          <ClientList
            clients={orderedClients}
            onPin={handlePin}
            tags={tags}
            onTagsChanged={onTagsChanged}
            onTagCreated={onTagCreated}
            groupBy={groupBy}
            totalCount={clients.length}
            pendingCounts={pendingCounts}
            onSelect={openClientDetails}
            onRoutine={openRoutineDialog}
            onEditRoutine={openDirectRoutineEditor}
            onDiet={openDietDialog}
            onSupplements={(client) => supplementDialog.open(client.id)}
            onRemove={handleRemoveClient}
            onCheckIn={(client) => checkInDialog.open(client.id)}
          />
        </>
      )}

      {/* Dialogs always rendered */}
      <Dialog open={showRoutineDialog} onOpenChange={(open) => { if (!open) routineDialog.close(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{t('myClients.assignRoutine')}</DialogTitle>
          </DialogHeader>

          {showCopyFromClient ? (
            <div className="py-2 space-y-3">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setShowCopyFromClient(false)}>
                  <ArrowLeft className="h-4 w-4 mr-1" /> {t('common.back')}
                </Button>
                <p className="text-sm font-medium">{t('myClients.copyRoutineFromAnotherClient')}</p>
              </div>
              <ScrollArea className="h-[320px]">
                <div className="space-y-2 pr-3">
                  {clientRoutinesList
                    .filter(r => r.client_id !== editClient?.id)
                    .map((r, idx) => (
                      <div
                        key={r.id}
                        className="p-3 rounded-xl border border-border/50 hover:border-primary/50 hover:bg-primary/90 cursor-pointer transition-all group"
                        onClick={() => handleCopyClientRoutine(r)}
                        data-testid={`copy-client-routine-${idx}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0">
                            <User className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm truncate">{r.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {r.client_name} • {r.days?.length || 0} days • {r.days?.reduce((a, d) => a + (d.exercises?.length || 0), 0)} exercises
                            </p>
                          </div>
                          <Copy className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                        </div>
                      </div>
                    ))}
                  {clientRoutinesList.filter(r => r.client_id !== editClient?.id).length === 0 && (
                    <p className="text-center text-muted-foreground py-8 text-sm">{t('myClients.noOtherClientRoutines')}</p>
                  )}
                </div>
              </ScrollArea>
            </div>
          ) : (
            <div className="py-4">
              <Label>{t('myClients.selectTemplateRoutineFor')} {editClient?.name}</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">{t('myClients.personalCopyNote')}</p>
              <Select value={assignRoutineId} onValueChange={setAssignRoutineId}>
                <SelectTrigger className="mt-2" data-testid="routine-select">
                  <SelectValue placeholder={t('myClients.selectRoutinePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('common.none')} ({t('clients.removeRoutine')})</SelectItem>
                  {routines.filter(r => !r.client_id).map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.name} ({r.days?.length || 0} days)</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {clientRoutinesList.filter(r => r.client_id !== editClient?.id).length > 0 && (
                <button
                  onClick={() => setShowCopyFromClient(true)}
                  className="w-full mt-3 p-3 rounded-xl border border-dashed border-primary/30 hover:border-primary/50 hover:bg-primary/90 text-sm text-muted-foreground hover:text-primary transition-all flex items-center justify-center gap-2"
                  data-testid="copy-from-client-btn"
                >
                  <Users className="h-4 w-4" />
                  {t('myClients.copyRoutineFromAnotherClient')}
                </button>
              )}
            </div>
          )}

          {!showCopyFromClient && (
            <DialogFooter>
              <Button variant="outline" onClick={() => routineDialog.close()}>{t('common.cancel')}</Button>
              <Button onClick={handleAssignRoutine} className="bg-primary hover:bg-primary/90" data-testid="save-routine-btn">{t('clients.assign')}</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {showDietDialog && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[200] bg-background flex flex-col">
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border sticky top-0 bg-background z-10">
            <Tooltip>
              <TooltipTrigger asChild>
                <button onClick={() => dietDialog.close()} className="p-1 rounded-md hover:bg-muted active:bg-muted transition-colors">
                  <ArrowLeft className="h-5 w-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{t('common.back')}</TooltipContent>
            </Tooltip>
            <h1 className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{t('clients.setDietTargets')}</h1>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            <p className="text-sm text-muted-foreground">{t('clients.setTargetsFor')} {editClient?.name}</p>

            {/* Coach Diet Activate/Deactivate Toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/30">
              <div>
                <p className="text-sm font-medium">{t('myClients.dietProtocol')}</p>
                <p className="text-xs text-muted-foreground">
                  {editClient?.coach_diet_active !== false ? t('myClients.clientFollowingDiet') : t('myClients.clientManagingOwnDiet')}
                </p>
              </div>
              <button
                onClick={() => {
                  const newActive = !editClient?.coach_diet_active;
                  toggleCoachDietMutation.mutate({ clientId: editClient.id, active: newActive });
                  setEditClient(prev => ({ ...prev, coach_diet_active: newActive }));
                }}
                className={`relative w-11 h-6 rounded-full transition-colors ${editClient?.coach_diet_active ? 'bg-primary' : 'bg-muted-foreground/30'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${editClient?.coach_diet_active ? 'translate-x-5' : ''}`} />
              </button>
            </div>

            <div className={editClient?.coach_diet_active === false ? 'opacity-40 pointer-events-none' : ''}>
            {/* Per-day toggle */}
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">{t('myClients.scheduleType')}</Label>
              <div className="flex items-center gap-1 bg-muted rounded-lg p-0.5">
                <button
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${!perDayMode ? 'bg-primary text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                  onClick={() => setPerDayMode(false)}
                >{t('myClients.sameEveryDay')}</button>
                <button
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${perDayMode ? 'bg-primary text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                  onClick={() => setPerDayMode(true)}
                >{t('myClients.customPerDay')}</button>
              </div>
            </div>

            {/* Shared grams/% toggle — shown for both modes */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">{t('clients.macros')}</Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => editTargetsDialog.open('targets')}
                      className="p-1 rounded-md hover:bg-muted active:bg-muted transition-colors text-muted-foreground hover:text-primary"
                    >
                      <Calculator className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{t('calculators.deficitSurplusTitle')}</TooltipContent>
                </Tooltip>
              </div>
              <div className="flex items-center gap-1 bg-muted rounded-lg p-0.5" data-testid="macro-mode-toggle">
                <button
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${macroMode === 'grams' ? 'bg-primary text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                  onClick={() => setMacroMode('grams')}
                  data-testid="macro-mode-grams-btn"
                >{t('clients.grams')}</button>
                <button
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${macroMode === 'percent' ? 'bg-primary text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                  onClick={() => {
                    setMacroMode('percent');
                    // Compute percents from current grams for same-every-day
                    const cal = dietTargets.target_calories || 1;
                    setMacroPercents({
                      protein: Math.round(dietTargets.target_protein * 4 / cal * 100) || 30,
                      carbs: Math.round(dietTargets.target_carbs * 4 / cal * 100) || 40,
                      fat: Math.round(dietTargets.target_fat * 9 / cal * 100) || 30,
                    });
                    // Compute per-day percents from current grams
                    setPerDayPercents(Object.fromEntries(DAYS.map(d => {
                      const day = dailyMacros[d] || {};
                      const dayCal = day.calories || 1;
                      return [d, {
                        protein: Math.round((day.protein || 0) * 4 / dayCal * 100) || 30,
                        carbs: Math.round((day.carbs || 0) * 4 / dayCal * 100) || 40,
                        fat: Math.round((day.fat || 0) * 9 / dayCal * 100) || 30,
                      }];
                    })));
                  }}
                  data-testid="macro-mode-percent-btn"
                >%</button>
              </div>
            </div>

            {perDayMode ? (
              /* ── Custom per day table ── */
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    {macroMode === 'grams' ? t('myClients.macrosAutoCalcHint') : t('myClients.setKcalPercentHint')}
                  </p>
                  <button
                    className="text-xs text-primary hover:underline flex-shrink-0"
                    onClick={() => {
                      const first = dailyMacros[DAYS[0]];
                      setDailyMacros(Object.fromEntries(DAYS.map(d => [d, { ...first }])));
                      const firstPct = perDayPercents[DAYS[0]];
                      setPerDayPercents(Object.fromEntries(DAYS.map(d => [d, { ...firstPct }])));
                    }}
                  >{t('myClients.copyMonToAll')}</button>
                </div>
                <div className="overflow-x-auto overflow-y-hidden -mx-1">
                  <table className="w-full text-xs min-w-[520px]">
                    <thead>
                      <tr className="text-muted-foreground">
                        <th className="text-left py-1 pl-1 font-medium w-10">Day</th>
                        <th className="text-center py-1 font-medium"><span className="flex items-center justify-center gap-1"><Flame className="h-3 w-3 text-primary" />Kcal</span></th>
                        <th className="text-center py-1 font-medium"><span className="flex items-center justify-center gap-1"><Beef className="h-3 w-3 text-red-500" />{t('diet.protein')} {macroMode === 'percent' ? '(%)' : '(g)'}</span></th>
                        <th className="text-center py-1 font-medium"><span className="flex items-center justify-center gap-1"><Wheat className="h-3 w-3 text-yellow-500" />{t('diet.carbs')} {macroMode === 'percent' ? '(%)' : '(g)'}</span></th>
                        <th className="text-center py-1 font-medium"><span className="flex items-center justify-center gap-1"><Droplets className="h-3 w-3 text-blue-500" />{t('diet.fat')} {macroMode === 'percent' ? '(%)' : '(g)'}</span></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/30">
                      {DAYS.map((day, i) => {
                        const d = dailyMacros[day] || { calories: 2000, protein: 150, carbs: 200, fat: 70 };
                        const pct = perDayPercents[day] || { protein: 30, carbs: 40, fat: 30 };
                        const totalPct = pct.protein + pct.carbs + pct.fat;
                        return (
                          <tr key={day} className="hover:bg-muted/30">
                            <td className="py-1 pl-1 font-semibold text-foreground/70">{DAY_LABELS[i]}</td>

                            {/* Kcal — always editable; in grams mode scales macros, in % mode recalcs grams */}
                            <td className="py-1 px-1">
                              <Input
                                type="number"
                                className="h-11 md:h-7 text-base md:text-xs text-center px-1"
                                value={calDraftPerDay[day] !== undefined ? calDraftPerDay[day] : (d.calories || 0)}
                                onFocus={() => setCalDraftPerDay(p => ({ ...p, [day]: String(d.calories || 0) }))}
                                onChange={(e) => setCalDraftPerDay(p => ({ ...p, [day]: e.target.value }))}
                                onBlur={(e) => {
                                  const newCal = parseFloat(e.target.value) || 0;
                                  setCalDraftPerDay(p => { const n = { ...p }; delete n[day]; return n; });
                                  if (newCal <= 0) return;
                                  if (macroMode === 'grams') {
                                    setDailyMacros(prev => {
                                      const old = prev[day] || {};
                                      const oldCal = old.calories || 1;
                                      const ratio = newCal / oldCal;
                                      return { ...prev, [day]: {
                                        ...old,
                                        calories: newCal,
                                        protein: Math.round((old.protein || 0) * ratio),
                                        carbs: Math.round((old.carbs || 0) * ratio),
                                        fat: Math.round((old.fat || 0) * ratio),
                                      }};
                                    });
                                  } else {
                                    setDailyMacros(prev => ({
                                      ...prev,
                                      [day]: {
                                        calories: newCal,
                                        protein: Math.round(pct.protein / 100 * newCal / 4),
                                        carbs: Math.round(pct.carbs / 100 * newCal / 4),
                                        fat: Math.round(pct.fat / 100 * newCal / 9),
                                      }
                                    }));
                                  }
                                }}
                              />
                            </td>

                            {/* P / C / F */}
                            {['protein', 'carbs', 'fat'].map(field => (
                              <td key={field} className="py-1 px-1">
                                {macroMode === 'grams' ? (
                                  <div>
                                    <Input
                                      type="number"
                                      className="h-11 md:h-7 text-base md:text-xs text-center px-1"
                                      value={d[field] || 0}
                                      onChange={(e) => {
                                        const val = parseFloat(e.target.value) || 0;
                                        setDailyMacros(prev => {
                                          const updated = { ...prev[day], [field]: val };
                                          updated.calories = calcCals(updated.protein, updated.carbs, updated.fat);
                                          return { ...prev, [day]: updated };
                                        });
                                      }}
                                    />
                                    <p className="text-xs text-muted-foreground text-center mt-0.5 font-mono leading-none">
                                      {(() => {
                                        const cal = d.calories || calcCals(d.protein, d.carbs, d.fat) || 0;
                                        if (!cal) return '0%';
                                        const kcal = field === 'fat' ? (d[field] || 0) * 9 : (d[field] || 0) * 4;
                                        return `${Math.round(kcal / cal * 100)}%`;
                                      })()}
                                    </p>
                                  </div>
                                ) : (
                                  <div>
                                    <Input
                                      type="number"
                                      className="h-11 md:h-7 text-base md:text-xs text-center px-1"
                                      value={pct[field] || 0}
                                      onChange={(e) => {
                                        const val = parseFloat(e.target.value) || 0;
                                        const balanced = rebalancePct(pct, field, val);
                                        setPerDayPercents(prev => ({ ...prev, [day]: balanced }));
                                        const cal = d.calories || 2000;
                                        setDailyMacros(prev => ({
                                          ...prev,
                                          [day]: {
                                            ...prev[day],
                                            protein: Math.round(balanced.protein / 100 * cal / 4),
                                            carbs: Math.round(balanced.carbs / 100 * cal / 4),
                                            fat: Math.round(balanced.fat / 100 * cal / 9),
                                          }
                                        }));
                                      }}
                                    />
                                    <p className="text-xs text-muted-foreground text-center mt-0.5 font-mono leading-none">
                                      {field === 'fat' ? Math.round(pct[field] / 100 * (d.calories || 2000) / 9) : Math.round(pct[field] / 100 * (d.calories || 2000) / 4)}g
                                    </p>
                                  </div>
                                )}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                      {/* Average row */}
                      {(() => {
                        const avgCal = Math.round(DAYS.reduce((s, d) => {
                          const day = dailyMacros[d] || {};
                          return s + (macroMode === 'grams' ? calcCals(day.protein, day.carbs, day.fat) : (day.calories || 0));
                        }, 0) / 7);
                        const avgP = Math.round(DAYS.reduce((s, d) => s + ((dailyMacros[d]?.protein) || 0), 0) / 7);
                        const avgC = Math.round(DAYS.reduce((s, d) => s + ((dailyMacros[d]?.carbs) || 0), 0) / 7);
                        const avgF = Math.round(DAYS.reduce((s, d) => s + ((dailyMacros[d]?.fat) || 0), 0) / 7);
                        return (
                          <tr className="border-t-2 border-border bg-muted/30 font-semibold text-foreground/80">
                            <td className="py-1.5 pl-1 text-primary text-xs font-bold uppercase tracking-wide">{t('myClients.avg')}</td>
                            <td className="py-1.5 text-center font-mono">{avgCal}</td>
                            <td className="py-1.5 text-center font-mono">{macroMode === 'percent' ? `${Math.round(DAYS.reduce((s,d) => s + (perDayPercents[d]?.protein||0), 0)/7)}%` : `${avgP}g`}</td>
                            <td className="py-1.5 text-center font-mono">{macroMode === 'percent' ? `${Math.round(DAYS.reduce((s,d) => s + (perDayPercents[d]?.carbs||0), 0)/7)}%` : `${avgC}g`}</td>
                            <td className="py-1.5 text-center font-mono">{macroMode === 'percent' ? `${Math.round(DAYS.reduce((s,d) => s + (perDayPercents[d]?.fat||0), 0)/7)}%` : `${avgF}g`}</td>
                          </tr>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
                {macroMode === 'percent' && (
                  <p className="text-xs text-muted-foreground">{t('myClients.alwaysSums100')}</p>
                )}
              </div>
            ) : (
              /* ── Same every day ── */
              <>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Flame className="h-4 w-4 text-primary" /> {t('diet.calories')}</Label>
                  <Input
                    type="number"
                    value={calDraftSame !== null ? calDraftSame : dietTargets.target_calories}
                    onFocus={() => setCalDraftSame(String(dietTargets.target_calories))}
                    onChange={(e) => setCalDraftSame(e.target.value)}
                    onBlur={(e) => {
                      const newCal = parseFloat(e.target.value) || 0;
                      setCalDraftSame(null);
                      if (newCal <= 0) return;
                      if (macroMode === 'grams') {
                        setDietTargets(p => {
                          const oldCal = p.target_calories || 1;
                          const ratio = newCal / oldCal;
                          return {
                            ...p,
                            target_calories: newCal,
                            target_protein: Math.round((p.target_protein || 0) * ratio),
                            target_carbs: Math.round((p.target_carbs || 0) * ratio),
                            target_fat: Math.round((p.target_fat || 0) * ratio),
                          };
                        });
                      } else {
                        setDietTargets(p => ({
                          ...p,
                          target_calories: newCal,
                          target_protein: Math.round(macroPercents.protein / 100 * newCal / 4),
                          target_carbs: Math.round(macroPercents.carbs / 100 * newCal / 4),
                          target_fat: Math.round(macroPercents.fat / 100 * newCal / 9),
                        }));
                      }
                    }}
                    data-testid="diet-calories-input"
                  />
                </div>

                {macroMode === 'grams' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      { field: 'target_protein', label: t('diet.protein'), icon: <Beef className="h-4 w-4 text-red-500" />, divisor: 4, testid: 'diet-protein-input' },
                      { field: 'target_carbs', label: t('diet.carbs'), icon: <Wheat className="h-4 w-4 text-yellow-500" />, divisor: 4, testid: 'diet-carbs-input' },
                      { field: 'target_fat', label: t('diet.fat'), icon: <Droplets className="h-4 w-4 text-blue-500" />, divisor: 9, testid: 'diet-fat-input' },
                    ].map(({ field, label, icon, divisor, testid }) => (
                      <div key={field} className="space-y-2">
                        <Label className="flex items-center gap-2">{icon} {label} (g)</Label>
                        <Input
                          type="number"
                          value={dietTargets[field]}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            setDietTargets(p => {
                              const updated = { ...p, [field]: val };
                              updated.target_calories = calcCals(updated.target_protein, updated.target_carbs, updated.target_fat);
                              return updated;
                            });
                          }}
                          data-testid={testid}
                        />
                        <p className="text-xs text-muted-foreground text-center font-mono">
                          {(() => {
                            const cal = dietTargets.target_calories || calcCals(dietTargets.target_protein, dietTargets.target_carbs, dietTargets.target_fat) || 0;
                            if (!cal) return '0%';
                            return `${Math.round((dietTargets[field] || 0) * divisor / cal * 100)}%`;
                          })()}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {[
                        { key: 'protein', label: t('diet.protein'), icon: <Beef className="h-4 w-4 text-red-500" />, divisor: 4, gramKey: 'target_protein', testid: 'diet-protein-percent-input' },
                        { key: 'carbs', label: t('diet.carbs'), icon: <Wheat className="h-4 w-4 text-yellow-500" />, divisor: 4, gramKey: 'target_carbs', testid: 'diet-carbs-percent-input' },
                        { key: 'fat', label: t('diet.fat'), icon: <Droplets className="h-4 w-4 text-blue-500" />, divisor: 9, gramKey: 'target_fat', testid: 'diet-fat-percent-input' },
                      ].map(({ key, label, icon, divisor, gramKey, testid }) => (
                        <div key={key} className="space-y-2">
                          <Label className="flex items-center gap-2">{icon} {label} (%)</Label>
                          <Input
                            type="number"
                            value={macroPercents[key]}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              const balanced = rebalancePct(macroPercents, key, val);
                              setMacroPercents(balanced);
                              const cal = dietTargets.target_calories || 2000;
                              setDietTargets(p => ({
                                ...p,
                                target_protein: Math.round(balanced.protein / 100 * cal / 4),
                                target_carbs: Math.round(balanced.carbs / 100 * cal / 4),
                                target_fat: Math.round(balanced.fat / 100 * cal / 9),
                              }));
                            }}
                            data-testid={testid}
                          />
                          <p className="text-xs text-muted-foreground text-center font-mono">{dietTargets[gramKey]}g</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground text-center">{t('myClients.alwaysSums100')}</p>
                  </div>
                )}
              </>
            )}

            {/* Diet End Date */}
            <div className="mt-4">
              <Label className="text-sm font-medium mb-1 block">{t('myClients.dietEndDate')} <span className="text-muted-foreground font-normal">{t('myClients.optional')}</span></Label>
              {/* Mode selector pills */}
              <div className="flex gap-1 mb-2">
                {['days', 'weeks', 'months', 'date'].map(mode => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => { setDietEndMode(mode); setDietEndValue(''); setDietEndDate(''); }}
                    className={`px-3 py-1 rounded-full text-xs font-medium capitalize transition-colors ${dietEndMode === mode ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
                  >
                    {mode.charAt(0).toUpperCase() + mode.slice(1)}
                  </button>
                ))}
              </div>
              {/* Date picker mode */}
              {dietEndMode === 'date' ? (
                <DateInput
                  className="w-full h-9"
                  value={dietEndValue}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={e => { setDietEndValue(e.target.value); setDietEndDate(e.target.value); }}
                />
              ) : (
                <div className="space-y-2">
                  {/* Preset chips */}
                  <div className="flex flex-wrap gap-1">
                    {(dietEndMode === 'days' ? [7, 14, 21, 30, 60, 90] :
                      dietEndMode === 'weeks' ? [2, 4, 6, 8, 12] :
                      [1, 2, 3, 6]).map(preset => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => { const v = String(preset); setDietEndValue(v); setDietEndDate(computeEndDate(dietEndMode, v)); }}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${dietEndValue === String(preset) ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                  {/* Custom number input */}
                  <input
                    type="number"
                    min="1"
                    placeholder={`Custom ${dietEndMode}…`}
                    className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    value={dietEndValue}
                    onChange={e => { setDietEndValue(e.target.value); setDietEndDate(computeEndDate(dietEndMode, e.target.value)); }}
                  />
                </div>
              )}
              {/* End date preview */}
              {dietEndDate && (
                <p className="text-xs text-muted-foreground mt-1">
                  {t('myClients.endsOn')} {formatDate(dietEndDate + 'T00:00:00')} · {Math.ceil((new Date(dietEndDate) - new Date()) / 86400000)} {t('myClients.daysFromToday')}
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <Button onClick={handleUpdateDiet} className="w-full bg-primary hover:bg-primary/90" data-testid="save-diet-btn">{t('clients.saveTargets')}</Button>
            </div>
            </div>

            {/* Diet Suggestions Section */}
            <div className="border-t border-border/50 pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    {t('clients.dietSuggestionsTitle')}
                  </h3>
                  <p className="text-xs text-muted-foreground">{t('clients.dietSuggestionsDesc')}</p>
                </div>
                <div className="flex gap-2 flex-wrap justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => editClient && setShowImportFromClient(true)}
                    data-testid="import-diet-from-client-btn"
                  >
                    <Download className="h-3.5 w-3.5 mr-1.5" /> {t('myClients.importFromClient')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => editClient && setShowApplyTemplate(true)}
                    data-testid="apply-diet-template-btn"
                  >
                    <Library className="h-3.5 w-3.5 mr-1.5" /> {t('myClients.applyTemplate')}
                  </Button>
                  <Button
                    size="sm"
                    className="bg-primary hover:bg-primary/90"
                    onClick={() => editClient && setShowCreateSuggestion(true)}
                    data-testid="create-diet-suggestion-btn"
                  >
                    <ClipboardList className="h-3.5 w-3.5 mr-1.5" /> {t('clients.create')}
                  </Button>
                </div>
              </div>
              {clientSuggestions.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-3">{t('clients.noSuggestionsYet')}</p>
              ) : (
                <div className="space-y-2">
                  {clientSuggestions.map(suggestion => (
                    <div key={suggestion.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50" data-testid={`suggestion-item-${suggestion.id}`}>
                      <div>
                        <p className="text-sm font-medium">{suggestion.name}</p>
                        {suggestion.days_of_week?.length > 0 && (
                          <p className="text-xs text-primary font-medium">
                            {suggestion.days_of_week.map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(' · ')}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {suggestion.total_calories_min != null && Math.round(suggestion.total_calories_min) !== Math.round(suggestion.total_calories)
                            ? `${Math.round(suggestion.total_calories_min)}–${Math.round(suggestion.total_calories)} kcal · P:${Math.round(suggestion.total_protein_min)}–${Math.round(suggestion.total_protein)}g · C:${Math.round(suggestion.total_carbs_min)}–${Math.round(suggestion.total_carbs)}g · F:${Math.round(suggestion.total_fat_min)}–${Math.round(suggestion.total_fat)}g`
                            : `${Math.round(suggestion.total_calories)} kcal · P:${Math.round(suggestion.total_protein)}g · C:${Math.round(suggestion.total_carbs)}g · F:${Math.round(suggestion.total_fat)}g`
                          }
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-11 w-11 md:h-7 md:w-7 text-muted-foreground" onClick={() => saveToTemplate(suggestion)} title="Save as template" data-testid={`save-template-${suggestion.id}`} tooltip={t('tooltips.saveAsTemplate')}>
                          <BookmarkPlus className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-11 w-11 md:h-7 md:w-7 text-muted-foreground" onClick={() => { suggestionDialog.open(suggestion.id); setShowCreateSuggestion(true); }} data-testid={`edit-suggestion-${suggestion.id}`} tooltip={t('tooltips.edit')}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-11 w-11 md:h-7 md:w-7 text-destructive" onClick={() => deleteDietSuggestion(suggestion.id)} data-testid={`delete-suggestion-${suggestion.id}`} tooltip={t('tooltips.delete')}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      <DeficitSurplusDialog
        open={showDeficitCalc}
        onClose={() => editTargetsDialog.close()}
        client={editClient}
        targetKcal={
          perDayMode
            ? Math.round(DAYS.reduce((s, d) => {
                const day = dailyMacros[d] || {};
                return s + (macroMode === 'grams' ? calcCals(day.protein, day.carbs, day.fat) : (day.calories || 0));
              }, 0) / 7)
            : dietTargets.target_calories
        }
      />

      <CreateDietSuggestionModal
        open={showCreateSuggestion}
        onOpenChange={(v) => { setShowCreateSuggestion(v); if (!v) { suggestionDialog.close(); } }}
        clientId={editClient?.id}
        clientName={editClient?.name}
        editingSuggestion={editingSuggestion}
        existingSuggestions={clientSuggestions}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['client-suggestions', editClient?.id] })}
      />

      <ApplyDietTemplateSheet
        open={showApplyTemplate}
        onOpenChange={setShowApplyTemplate}
        clientId={editClient?.id}
        clientName={editClient?.name}
        existingSuggestions={clientSuggestions}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['client-suggestions', editClient?.id] })}
      />

      <ImportDietFromClientSheet
        open={showImportFromClient}
        onOpenChange={setShowImportFromClient}
        targetClientId={editClient?.id}
        targetClientName={editClient?.name}
        allClients={clients}
        existingSuggestions={clientSuggestions}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['client-suggestions', editClient?.id] })}
      />

      <ClientCheckInsSheet
        client={checkInClient}
        open={!!checkInClient}
        onClose={() => checkInDialog.close()}
      />

      {/* Remove Client Confirmation */}
      <AlertDialog open={!!removeConfirmClient} onOpenChange={(open) => { if (!open) setRemoveConfirmClient(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('myClients.removeClient')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('myClients.removeClientConfirm', { name: removeConfirmClient?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-remove-client">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemoveClient} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-remove-client">
              {t('common.remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Supplement Plan Sheet */}
      <SupplementPlanSheet
        client={supplementClient}
        open={!!supplementClient}
        onClose={() => supplementDialog.close()}
      />

      {/* Direct Routine Editor */}
      <RoutineEditorFullScreen
        isOpen={!!directRoutineEditor}
        onClose={() => routineEditDialog.close()}
        routine={directRoutineEditor?.routine}
        clientUser={directRoutineEditor?.clientUser}
        onShowHistory={() => directRoutineEditor?.routine?.id && historyDialog.open(directRoutineEditor.routine.id)}
        onChangeRoutine={() => {
          const cu = directRoutineEditor.clientUser;
          routineEditDialog.close();
          openRoutineDialog(cu);
        }}
        onSave={async (editedRoutine, { sessionId } = {}) => {
          await saveDirectRoutineMutation.mutateAsync({
            clientId: directRoutineEditor.clientUser.id,
            editedRoutine,
            sessionId,
          });
          setDirectRoutineEditor(prev => prev ? {
            ...prev,
            routine: { ...prev.routine, ...editedRoutine },
          } : null);
        }}
        onSaveAsTemplate={async () => {
          const routineId = directRoutineEditor?.routine?.id;
          if (!routineId) return;
          try {
            await client.post(`/routines/${routineId}/save-as-template`);
            toast.success(t('clients.routineSavedAsTemplate'));
            queryClient.invalidateQueries({ queryKey: ['my-clients-and-routines'] });
          } catch {
            toast.error(t('clients.failedSaveTemplate'));
          }
        }}
      />

      <RoutineHistoryDialog
        open={historyDialog.isOpen}
        routineId={historyDialog.id}
        onClose={() => historyDialog.close()}
        queryKeysToInvalidate={[['my-clients-and-routines'], ['routines']]}
      />

      <ManageTagsModal
        open={manageTagsOpen}
        onClose={() => setManageTagsOpen(false)}
        tags={tags}
        onTagsChanged={onTagsChanged}
      />
    </>
  );
}
